import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { forgotPassword } from "../lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch {
      // Endpoint always succeeds (no account enumeration) — treat errors as sent too.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <img src="/branding/tow-hook-icon-1024.png" alt="Tow-Hook" className="h-14 w-14 rounded-xl" />
          <div className="font-display font-black text-3xl tracking-tight text-white leading-none">Tow-Hook</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-xl">
          {sent ? (
            <div data-testid="forgot-password-sent">
              <MailCheck className="h-10 w-10 text-emerald-600 mb-3" />
              <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Check your email</h1>
              <p className="text-sm text-slate-500 mt-2">
                If an account exists for <strong>{email}</strong>, we've sent a link to reset your
                password. The link expires in 60 minutes.
              </p>
              <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-slate-900 hover:text-blue-600" data-testid="forgot-back-to-login">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Forgot password?</h1>
              <p className="text-sm text-slate-500 mt-1">
                Enter your email and we'll send you a link to reset it.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label className="uppercase-label">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="forgot-email"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                  data-testid="forgot-submit"
                >
                  {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : "Send reset link"}
                </Button>
              </form>
              <Link to="/login" className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="forgot-back-to-login">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
